import Skeleton from '@mui/material/Skeleton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useModuleObject from '@pages/modules/hooks/useModuleObject';
import { dateFormat, REQUEST_STATUS } from '@shared/constants/otherConstants';
import { getTestCasesRequest, updateTestCasesFromAddDialog } from '@pages/testcases/store';
import {
    selectDeleteTestCaseStatus,
    selectDuplicateTestCaseStatus,
    selectGetTestCasesStatus,
    selectTestCases,
    selectTestCasesCount,
} from '@pages/testcases/store/selectors';
import { useDispatch, useSelector } from 'react-redux';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { usePrevious } from '@shared/hooks/usePrevious';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    UI_FUNCTIONAL_TESTING_PATH,
    UI_SCHEMES_PATH,
    UI_TEST_CASE_PATH,
    UI_TEST_PLAN_PATH,
} from '@shared/constants/pathsConstants';
import dayjs from 'dayjs';
import TestPlansPopover from './TestPlansPopover';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import { TESTCASE_STATUS } from '@pages/testcases/constants/testcasesСonstants';
import { convertToTranslationKey, toTitleCase } from '@shared/utils/utilsOld';
import CustomTable from '@shared/components/others/CustomTable/CustomTable';
import { useListState } from '@shared/hooks/useListState';
import UtmsChip from '@shared/components/ui/UtmsChip';
import OverflowChipListForTable from '@shared/components/others/OverflowChipListForTable/OverflowChipListForTable';
import UtmsPagination from '@shared/components/ui/UtmsPagination';
import { useTestcasesChunk } from '@pages/testcases/hooks/useTestcasesChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import {
    selectAddTestcasesToTestplanStatus,
    selectDeleteTestcasesFromTestplanStatus,
    selectGetTestCasesFilteredByTestPlanStatus,
    selectTestCasesFilteredByTestPlan,
    selectTestCasesFilteredByTestPlanCount,
} from '@pages/testplans/store/selectors';
import { getTestCasesFilteredByTestPlanRequest } from '@pages/testplans/store';
import { useCreatedByUserColumn } from '@shared/hooks/useCreatedByUserColumn';
import { useTestplansChunk } from '@pages/testplans/hooks/useTestplansChunk';
import { useMultiPageSelection } from '@shared/hooks/useMultiPageSelection';
import { getDefectLinkById } from '@shared/utils/utils';
import DefectsPopover from '@pages/defects/components/DefectsPopover';
import { selectGetTeamsStatus, selectTeams } from '@features/teams/store/selectors';
import { getTeamsRequest } from '@features/teams/store';

const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const TestcasesTable = (props) => (
    <ComponentWithChunks
        featuresArray={['testCases', 'testPlans']}
        Component={TestCasesTableComponent}
        hooksArray={[useTestcasesChunk, useTestplansChunk]}
        {...props}
    />
);

const TestCasesTableComponent = ({
    dialogMode,
    toolbar,
    testCasesProp,
    setSelectedTestCaseId = undefined,
    setOpenAddTestcasesDialog = undefined,
    refSetRowSelection = undefined,
    fullModuleName,
    moduleNameFromUrlParams,
    tagsSearch,
    setSelectedTestCasesIds,
    testplanId,
    enableMultiRowSelection = false,
    defectId,
    customColumns,
}) => {
    const modulesMode = fullModuleName !== undefined;
    const { testplanId: testplanIdFromUrl } = useParams();
    const shortMode = Boolean(defectId);
    const {
        rowSelection,
        setRowSelection,
        pagination,
        setPagination,
        globalFilter,
        setGlobalFilter,
        showColumnFilters,
        setShowColumnFilters,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        fetchData,
    } = useListState(
        !testplanId ? (modulesMode ? 'testCasesTableForModules' : 'testCasesTable') : '',
        testplanId === 'create',
        testplanId && testplanId !== 'create' ? getTestCasesFilteredByTestPlanRequest : getTestCasesRequest,
        {
            fullModuleName,
            tags: tagsSearch,
            module: moduleNameFromUrlParams,
            testplanId,
            disableUrlParams: dialogMode || shortMode,
            defectId,
        },
    );

    if (refSetRowSelection) {
        refSetRowSelection.current = setRowSelection;
    }

    const dispatch = useDispatch();
    const getTestcasesStatus = useSelector(selectGetTestCasesStatus);
    const getTestCasesFilteredByTestPlanStatus = useSelector(selectGetTestCasesFilteredByTestPlanStatus);
    const testCases = useSelector(selectTestCases);
    const testCasesFilteredByTestPlan = useSelector(selectTestCasesFilteredByTestPlan);
    const testcasesCount = useSelector(selectTestCasesCount);
    const teams = useSelector(selectTeams);
    const getTeamsStatus = useSelector(selectGetTeamsStatus);
    const testCasesFilteredByTestPlanCount = useSelector(selectTestCasesFilteredByTestPlanCount);
    const navigate = useNavigate();
    const [testPlansPopoverAnchorEl, setTestPlansPopoverAnchorEl] = useState(null);
    const [prevSelectedStaticRowIndex, setPrevSelectedStaticRowIndex] = useState(null);
    const { t } = useTranslation();
    const handTest = t('hand-test');
    const autoTest = t('auto-test');
    const [testPlansByTestCase, setTestPlansByTestCase] = useState([]);
    const deleteTestCaseStatus = useSelector(selectDeleteTestCaseStatus);
    const prevDeleteTestCaseStatus = usePrevious(deleteTestCaseStatus);
    const duplicateTestCaseStatus = useSelector(selectDuplicateTestCaseStatus);
    const prevDuplicateTestCaseStatus = usePrevious(duplicateTestCaseStatus);
    const testCasesView = useMemo(() => {
        return testplanId ? testCasesFilteredByTestPlan : testCases || [];
    }, [testCases, testCasesFilteredByTestPlan, testplanId]);
    const createdByUserColumn = useCreatedByUserColumn({ columnFilters, setColumnFilters });
    const tagsSearchIds = useMemo(() => tagsSearch && tagsSearch.map((item) => item.pk), [tagsSearch]);
    const deleteTestcasesFromTestplanStatus = useSelector(selectDeleteTestcasesFromTestplanStatus);
    const prevDeleteTestcasesFromTestplanStatus = usePrevious(deleteTestcasesFromTestplanStatus);
    const addTestcasesToTestplanStatus = useSelector(selectAddTestcasesToTestplanStatus);
    const prevAddTestcasesToTestplanStatus = usePrevious(addTestcasesToTestplanStatus);
    const [defectsPopoverAnchorEl, setDefectsPopoverAnchorEl] = useState(null);
    const [defectsPopoverByTestcase, setDefectsPopoverByTestcase] = useState(null);

    const handleOpenDefectsPopover = (e, defects) => {
        e.stopPropagation();
        setDefectsPopoverAnchorEl(e.currentTarget);
        setDefectsPopoverByTestcase(defects);
    };

    const testCasesTransformData = useMemo(
        () =>
            Boolean(testCasesProp) && tagsSearchIds?.length
                ? testCasesView
                      .map((item) => ({
                          ...item,
                          type: item.is_handtest ? handTest : autoTest,
                      }))
                      .filter((item) => {
                          const arrTagsIds = item.tags.map((item) => item.pk);
                          for (let i = 0; i < tagsSearchIds.length; i += 1) {
                              if (!arrTagsIds.includes(tagsSearchIds[i])) {
                                  return false;
                              }
                          }
                          return true;
                      })
                : testCasesView.map((item) => ({
                      ...item,
                      type: item.is_handtest ? handTest : autoTest,
                  })),
        [autoTest, handTest, tagsSearchIds, testCasesProp, testCasesView],
    );

    const {
        rowSelectionForPage: rowSelectionFromAllSelected,
        handleSelectionChange: handleRowSelectionChange,
        seedIds,
        allSelectedObjects,
        reset: resetSelection,
    } = useMultiPageSelection(testCasesTransformData);

    useEffect(() => {
        if (
            duplicateTestCaseStatus === REQUEST_STATUS.SUCCESS &&
            prevDuplicateTestCaseStatus !== REQUEST_STATUS.SUCCESS &&
            testCasesTransformData.length > 0
        ) {
            const duplicateId = testCasesTransformData[0].pk;
            !rowSelection[duplicateId] && setRowSelection({ [testCasesTransformData[0].pk]: true });
        }
    }, [duplicateTestCaseStatus, prevDuplicateTestCaseStatus, testCasesTransformData, rowSelection, setRowSelection]);

    const isTestcaseFromTestplan = useCallback(
        (testcase) => {
            return testcase && testcase.testplans.some((testplan) => Number(testplanIdFromUrl) === testplan.pk);
        },
        [testplanIdFromUrl],
    );

    useEffect(() => {
        if (!dialogMode) return;

        resetSelection();

        const alreadyInTestplan = testCases.filter((tc) => isTestcaseFromTestplan(tc));
        seedIds(
            alreadyInTestplan.map((tc) => tc.pk),
            alreadyInTestplan,
        );
    }, [dialogMode, isTestcaseFromTestplan, resetSelection, testCases, seedIds]);

    useEffect(() => {
        if (!dialogMode) return;
        const toAdd = allSelectedObjects.filter((tc) => !isTestcaseFromTestplan(tc));
        dispatch(updateTestCasesFromAddDialog(toAdd));
    }, [dialogMode, dispatch, isTestcaseFromTestplan, allSelectedObjects]);

    useEffect(() => {
        if (setSelectedTestCaseId) {
            const firstSelected = Object.keys(rowSelectionFromAllSelected)[0];
            setSelectedTestCaseId(firstSelected ? Number(firstSelected) : null);
        }
        if (setSelectedTestCasesIds) {
            setSelectedTestCasesIds(
                Object.keys(rowSelectionFromAllSelected)
                    .map((key) => rowSelectionFromAllSelected[key] && Number(key))
                    .filter(Boolean),
            );
        }
    }, [rowSelection, setSelectedTestCaseId, rowSelectionFromAllSelected, setSelectedTestCasesIds]);

    const handleClickScheme = useCallback(
        (id) => (e) => {
            e.preventDefault();
            id && navigate(`/${UI_SCHEMES_PATH}/${id}`);
        },
        [navigate],
    );

    const handleOpenTestPlansPopover = useCallback(
        (testplans) => (e) => {
            e.stopPropagation();
            setTestPlansPopoverAnchorEl(e.currentTarget);
            setTestPlansByTestCase(testplans);
        },
        [],
    );

    const updateTableDataOnSuccessStatus = useCallback(
        (action) => {
            if (setSelectedTestCasesIds || dialogMode) {
                setRowSelection({});
                resetSelection();
            }
            setSelectedTestCaseId && setSelectedTestCaseId(null);
            fetchData(action);
        },
        [dialogMode, fetchData, resetSelection, setRowSelection, setSelectedTestCaseId, setSelectedTestCasesIds],
    );

    const updateDataOnSuccess = useCallback(
        (status, prevStatus, action) => {
            if (status === REQUEST_STATUS.SUCCESS && prevStatus !== status) {
                updateTableDataOnSuccessStatus(action);
            }
        },
        [updateTableDataOnSuccessStatus],
    );

    const handlePaginationChange = useCallback(
        (updater) => {
            setPagination(updater);
            setPrevSelectedStaticRowIndex(null);
        },
        [setPagination],
    );

    useEffect(() => {
        updateDataOnSuccess(deleteTestCaseStatus, prevDeleteTestCaseStatus, 'delete');
        updateDataOnSuccess(duplicateTestCaseStatus, prevDuplicateTestCaseStatus, 'duplicate');
        updateDataOnSuccess(
            deleteTestcasesFromTestplanStatus,
            prevDeleteTestcasesFromTestplanStatus,
            'delete testcases',
        );
        if (testplanId) {
            updateDataOnSuccess(addTestcasesToTestplanStatus, prevAddTestcasesToTestplanStatus, 'add testcases');
        }
    }, [
        addTestcasesToTestplanStatus,
        deleteTestCaseStatus,
        deleteTestcasesFromTestplanStatus,
        duplicateTestCaseStatus,
        prevAddTestcasesToTestplanStatus,
        prevDeleteTestCaseStatus,
        prevDeleteTestcasesFromTestplanStatus,
        prevDuplicateTestCaseStatus,
        updateDataOnSuccess,
        testplanId,
    ]);

    useEffect(() => {
        setPrevSelectedStaticRowIndex(null);
    }, [testCasesTransformData]);

    //should be memoized or stable
    const columns = useMemo(
        () => [
            {
                accessorKey: 'pk',
                header: 'ID',
                size: 75,
            },
            {
                accessorKey: 'name',
                header: toTitleCase(t('test_case')),
                size: 200,
                Cell: ({ cell }) => {
                    const testCase = cell.row.original;
                    const clickableName = testCase && (
                        <Link
                            underline="hover"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testCase.pk}`);
                            }}
                            sx={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${testCase.pk}`}
                        >
                            {testCase.name}
                        </Link>
                    );
                    return (
                        <OverflowTooltip placement="bottom" title={testCase.name}>
                            <Typography component={'span'}>{!dialogMode ? clickableName : testCase.name}</Typography>
                        </OverflowTooltip>
                    );
                },
            },
            {
                accessorKey: 'module',
                header: t('module'),
                Cell: ({ cell }) => {
                    const module = cell.row.original.module;
                    const moduleObject = useModuleObject(module);

                    if (!module) {
                        return <WarningIcon color="error" />;
                    } else if (!moduleObject) {
                        return <Skeleton width={50} />;
                    }

                    return (
                        <OverflowTooltip placement="bottom" title={moduleObject['path']}>
                            {moduleObject['path']}
                        </OverflowTooltip>
                    );
                },
                enableColumnFilter: false,
                size: 120,
            },
            {
                accessorKey: 'type',
                header: t('type'),
                size: 100,
                filterVariant: 'select',
                filterSelectOptions: [autoTest, handTest],
                enableSorting: false,
            },

            {
                accessorKey: 'status',
                header: t('status'),
                size: 130,
                Cell: ({ cell }) => {
                    const status = cell.getValue();

                    return (
                        status && (
                            <UtmsChip
                                label={t(convertToTranslationKey(status))}
                                color={status === 'Approved' ? 'success' : 'default'}
                            />
                        )
                    );
                },
                filterVariant: 'select',
                filterSelectOptions: Object.keys(TESTCASE_STATUS).map((item) => ({
                    label: (
                        <UtmsChip
                            label={toTitleCase(t(convertToTranslationKey(TESTCASE_STATUS[item])))}
                            color={item === 'APPROVED' ? 'success' : 'default'}
                        />
                    ),
                    value: item,
                })),
            },
            {
                accessorKey: 'team',
                header: t('team'),
                size: 130,
                Cell: ({ cell }) => {
                    const team = cell.getValue();
                    return team && <UtmsChip label={team} />;
                },
                muiFilterAutocompleteProps: {
                    onOpen: () => dispatch(getTeamsRequest()),
                    loading: getTeamsStatus === REQUEST_STATUS.LOADING,
                    forcePopupIcon: true,
                },
                filterVariant: 'autocomplete',
                filterSelectOptions: teams.map((team) => ({
                    label: team,
                    value: team,
                })),
            },
            {
                id: 'objective',
                header: t('objective'),
                maxSize: 200,
                accessorFn: (row) => {
                    const paragraphs = row?.objective?.blocks?.filter((block) => block.type === 'paragraph');

                    if (Array.isArray(paragraphs)) {
                        const paragraphText = paragraphs.reduce((acc, element) => {
                            return acc + element.data.text;
                        }, '');
                        return paragraphText;
                    } else {
                        return '';
                    }
                },
                Cell: ({ cell }) => {
                    const paragraphs = cell.row.original?.objective?.blocks?.filter(
                        (block) => block.type === 'paragraph',
                    );

                    const objectiveText = Array.isArray(paragraphs) && paragraphs.map((pr) => pr.data.text).join('\n');
                    const plainText = objectiveText ? stripHtml(objectiveText) : '';

                    if (!objectiveText) {
                        return '—';
                    }

                    // we can use dangerouslySetInnerHTML, because EditorJs uses sanitizer inside
                    return (
                        <Tooltip
                            placement="bottom"
                            title={plainText.length > 70 || paragraphs.length > 3 ? plainText : ''}
                        >
                            <Box
                                className="third-level-truncate"
                                sx={{
                                    fontSize: '0.9rem',
                                    whiteSpace: 'break-spaces',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: objectiveText,
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
            ...(!dialogMode
                ? [
                      {
                          accessorKey: 'test_plans',
                          header: t('test_plans'),
                          maxSize: 140,
                          Cell: ({ cell }) => {
                              const value = cell.row.original.testplans;
                              if (!value || !value.length) {
                                  return '—';
                              }
                              if (value.length === 1)
                                  return (
                                      <OverflowTooltip placement="bottom" title={value[0].title}>
                                          <Link
                                              underline="hover"
                                              onClick={(e) => {
                                                  e.preventDefault();
                                                  navigate(
                                                      `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${value[0].pk}`,
                                                  );
                                              }}
                                              href={`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}/${value[0].pk}`}
                                              sx={{ cursor: 'pointer' }}
                                          >
                                              {value[0].title}
                                          </Link>
                                      </OverflowTooltip>
                                  );
                              return (
                                  <Link
                                      underline="hover"
                                      onClick={handleOpenTestPlansPopover(value)}
                                      sx={{ cursor: 'pointer' }}
                                  >
                                      {t('testplanWithCount', { count: value.length })}
                                  </Link>
                              );
                          },
                          enableColumnFilter: false,
                          enableSorting: false,
                      },
                      {
                          accessorKey: 'scheme',
                          header: t('scheme'),
                          maxSize: 133,
                          Cell: ({ cell }) => {
                              const value = cell.row.original.scheme;
                              if (!value) {
                                  return '—';
                              }

                              return (
                                  <OverflowTooltip placement="bottom" title={value.name}>
                                      <Link
                                          underline="hover"
                                          onClick={handleClickScheme(value.pk)}
                                          sx={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                          href={`/${UI_SCHEMES_PATH}/${value.pk}`}
                                      >
                                          {value.name}
                                      </Link>
                                  </OverflowTooltip>
                              );
                          },
                          enableSorting: false,
                      },
                  ]
                : []),
            {
                accessorKey: 'tags',
                header: t('tags'),
                size: 100,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ cell }) => {
                    const tags = cell.getValue();
                    if (!tags || !tags.length) {
                        return '—';
                    }
                    return (
                        <OverflowChipListForTable
                            array={tags}
                            getId={(item) => item.pk}
                            getLabel={(item) => item.text}
                            chipMaxWidth={cell.column.getSize() - 16}
                            chipVariant="filled"
                        />
                    );
                },
            },
            {
                accessorKey: 'defects',
                header: t('defects'),
                size: 110,
                Cell: ({ cell }) => {
                    const testcase = cell.row.original;
                    const defects = cell.getValue();
                    const defect = defects[0];

                    if (!defects || !defects.length) {
                        return '–';
                    }

                    return (
                        <>
                            {defects.length > 1 ? (
                                <Link
                                    underline="hover"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={(e) => handleOpenDefectsPopover(e, testcase.defects)}
                                >
                                    {t('defectsWithCount', { count: defects.length })}
                                </Link>
                            ) : (
                                <OverflowTooltip title={defect.name}>
                                    <Link
                                        underline="hover"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(getDefectLinkById(defect.pk));
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                        href={getDefectLinkById(defect.pk)}
                                    >
                                        {defect.name}
                                    </Link>
                                </OverflowTooltip>
                            )}
                        </>
                    );
                },
                enableColumnFilter: false,
                enableSorting: false,
            },
            {
                accessorKey: 'createdAt',
                header: toTitleCase(t('created')),
                maxSize: 160,
                Cell: ({ cell }) => {
                    const date = cell.row.original['createdAt'];
                    return date ? dayjs(date).format(dateFormat) : t('no_info');
                },
                filterVariant: 'date',
            },
            createdByUserColumn,
        ],
        [
            autoTest,
            dialogMode,
            handTest,
            createdByUserColumn,
            handleClickScheme,
            handleOpenTestPlansPopover,
            dispatch,
            getTeamsStatus,
            teams,
            navigate,
            t,
        ],
    );

    const isLoading = useMemo(
        () =>
            (!testplanId && getTestcasesStatus === REQUEST_STATUS.LOADING) ||
            (testplanId && getTestCasesFilteredByTestPlanStatus === REQUEST_STATUS.LOADING),
        [getTestCasesFilteredByTestPlanStatus, getTestcasesStatus, testplanId],
    );

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: shortMode
                        ? '100%'
                        : dialogMode
                          ? 600
                          : `calc(100vh - ${modulesMode ? 270 : testplanId ? 409 : 178}px)`,
                    maxHeight: shortMode ? 320 : undefined,
                }}
            >
                <CustomTable
                    tableId="testcases-table"
                    getRowId={(row) => String(row.pk)}
                    rowCount={!testplanId ? testcasesCount : testCasesFilteredByTestPlanCount}
                    columns={
                        customColumns ? columns.filter((column) => customColumns.includes(column.accessorKey)) : columns
                    }
                    data={testCasesTransformData}
                    dialogMode={dialogMode}
                    state={{
                        showSkeletons: isLoading,
                        pagination,
                        globalFilter,
                        columnFilters,
                        sorting,
                        rowSelection: rowSelectionFromAllSelected,
                        showColumnFilters,
                    }}
                    displayColumnDefOptions={{
                        'mrt-row-select': {
                            header: '',
                            size: 45,
                            minSize: 0,
                        },
                    }}
                    toolbar={toolbar}
                    enableHiding={!dialogMode}
                    muiTableHeadRowProps={{
                        sx: {
                            boxShadow: 0,
                            backgroundColor: 'background.paper',
                        },
                    }}
                    muiBottomToolbarProps={{
                        sx: {
                            minHeight: 0,
                            boxShadow: 0,
                            backgroundColor: 'inherit',
                        },
                    }}
                    renderBottomToolbar={() => (
                        <UtmsPagination
                            view="table"
                            pagination={pagination}
                            count={testplanId ? testCasesFilteredByTestPlanCount : testcasesCount}
                            handleChangePagination={setPagination}
                            sx={{ mt: 0.5 }}
                        />
                    )}
                    muiTableContainerProps={{
                        sx: {
                            maxHeight: `calc(100% - ${dialogMode || testplanId ? 7 : 10}rem)`,
                            height: '100%',
                            scrollbarGutter: 'stable',
                        },
                    }}
                    enableRequestsOnChangeState={!testCasesProp}
                    variant={modulesMode || dialogMode || shortMode ? 'elevation' : 'outlined'}
                    onPaginationChange={handlePaginationChange}
                    onGlobalFilterChange={setGlobalFilter}
                    onColumnFiltersChange={setColumnFilters}
                    onShowColumnFiltersChange={setShowColumnFilters}
                    onSortingChange={setSorting}
                    enableRowSelection={dialogMode || testplanId}
                    enableMultiRowSelection={enableMultiRowSelection}
                    enableSelectAll={false}
                    muiSelectCheckboxProps={({ row }) => {
                        const isSelectedFromTestPlan = row.original.testplans.some(
                            (testplan) => Number(testplanIdFromUrl) === testplan.pk,
                        );
                        return {
                            sx: {
                                marginTop: '0.25rem',
                            },
                            disabled: dialogMode && isSelectedFromTestPlan,
                        };
                    }}
                    muiTableBodyRowProps={({ row, staticRowIndex, table }) => {
                        const isSelectedFromTestPlan = row.original.testplans && isTestcaseFromTestplan(row.original);
                        const enableSelectOnClickItem =
                            (setSelectedTestCaseId || dialogMode || setSelectedTestCasesIds) && !isLoading;
                        return {
                            onClick: enableSelectOnClickItem
                                ? (e) => {
                                      e.stopPropagation();

                                      if (isSelectedFromTestPlan && dialogMode) return;
                                      const withShiftKey = e.shiftKey && enableMultiRowSelection;
                                      const isSelected = !!rowSelectionFromAllSelected[row.id];

                                      if (withShiftKey && prevSelectedStaticRowIndex !== null) {
                                          const start = Math.min(prevSelectedStaticRowIndex, staticRowIndex);
                                          const end = Math.max(prevSelectedStaticRowIndex, staticRowIndex);
                                          const rows = table.getRowModel().rows;
                                          const shouldSelect = !isSelected;

                                          for (let i = start; i <= end; i++) {
                                              const id = rows[i]?.id;
                                              const original = rows[i]?.original;
                                              if (
                                                  dialogMode &&
                                                  original?.testplans?.some((tp) => Number(testplanIdFromUrl) === tp.pk)
                                              ) {
                                                  continue;
                                              }
                                              if (id !== undefined) {
                                                  handleRowSelectionChange({ [id]: shouldSelect });
                                              }
                                          }
                                      } else {
                                          if (!enableMultiRowSelection) {
                                              const resetAll = {};
                                              Object.keys(rowSelectionFromAllSelected).forEach((id) => {
                                                  resetAll[id] = false;
                                              });
                                              if (Object.keys(resetAll).length) {
                                                  handleRowSelectionChange(resetAll);
                                              }

                                              if (!isSelected) {
                                                  handleRowSelectionChange({ [row.id]: true });
                                              }
                                          } else {
                                              handleRowSelectionChange({ [row.id]: !isSelected });
                                          }
                                      }

                                      setPrevSelectedStaticRowIndex(staticRowIndex);
                                  }
                                : undefined,
                            onDoubleClick: (e) => {
                                e.stopPropagation();
                                navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${row.original.pk}`);
                            },
                            onMouseDown: (e) => {
                                if (e.shiftKey) e.preventDefault();
                            },
                            sx: {
                                cursor: enableSelectOnClickItem && 'pointer',
                                backgroundColor: 'transparent !important',
                            },
                            selected: !!rowSelectionFromAllSelected[row.id],
                        };
                    }}
                    onRowSelectionChange={(updater) => {
                        const next = typeof updater === 'function' ? updater(rowSelectionFromAllSelected) : updater;

                        if (!enableMultiRowSelection) {
                            const newlySelected = Object.entries(next).find(
                                ([id, selected]) => selected && !rowSelectionFromAllSelected[id],
                            );

                            const resetAll = {};
                            Object.keys(rowSelectionFromAllSelected).forEach((id) => {
                                resetAll[id] = false;
                            });
                            handleRowSelectionChange(resetAll);
                            if (newlySelected) {
                                handleRowSelectionChange({ [newlySelected[0]]: true });
                            }
                            return;
                        }

                        const diff = {};
                        Object.entries(next).forEach(([id, selected]) => {
                            if (selected && !rowSelectionFromAllSelected[id]) diff[id] = true;
                        });
                        Object.keys(rowSelectionFromAllSelected).forEach((id) => {
                            if (rowSelectionFromAllSelected[id] && !next[id]) diff[id] = false;
                        });
                        if (Object.keys(diff).length) handleRowSelectionChange(diff);
                    }}
                    defaultColumn={{
                        muiTableBodyCellProps: {
                            sx: {
                                alignItems: 'flex-start',
                                fontSize: 14,
                                whiteSpace: 'wrap',
                                ':has(> .MuiCheckbox-root)': {
                                    pt: 0,
                                },
                                p: '8px 16px',
                            },
                        },
                    }}
                    muiTablePaperProps={{
                        elevation: 0,
                        sx: {
                            backgroundColor: 'background.paper',
                            height: dialogMode ? 600 : '100%',
                            minWidth: modulesMode || shortMode ? undefined : 1100,
                            p: modulesMode || shortMode ? undefined : 2,
                        },
                        variant: modulesMode ? undefined : 'outlined',
                    }}
                    {...(shortMode
                        ? {
                              muiTableContainerProps: {
                                  sx: {
                                      maxHeight: 207,
                                      maxWidth: '100%',
                                  },
                              },
                              muiTablePaperProps: {
                                  sx: {
                                      minWidth: '100%',
                                  },
                              },
                          }
                        : {})}
                    renderEmptyRowsFallback={() =>
                        testplanId && !testCasesFilteredByTestPlanCount && !dialogMode ? (
                            testplanId === 'create' ? (
                                <Typography
                                    sx={{
                                        color: 'var(--text-secondary)',
                                        fontStyle: 'italic',
                                        p: 2,
                                        pt: 1,
                                        display: 'flex',
                                        fontSize: 14,
                                    }}
                                    variant="body2"
                                >
                                    {t('save_testplan_to_add_testcases')}...
                                </Typography>
                            ) : (
                                <Typography sx={{ fontSize: 14, p: 2, pt: 1 }} variant="body2">
                                    {t('no_test_cases_added_yet')}.{' '}
                                    <Link
                                        underline="hover"
                                        onClick={() => setOpenAddTestcasesDialog && setOpenAddTestcasesDialog(true)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {`${t('add')} ${t('test_case').toLowerCase()}...`}
                                    </Link>
                                </Typography>
                            )
                        ) : (
                            <Box
                                sx={{
                                    color: 'var(--text-secondary)',
                                    fontStyle: 'italic',
                                    textAlign: 'center',
                                    verticalAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {t('no_record_to_display')}
                            </Box>
                        )
                    }
                />
            </Box>
            <TestPlansPopover
                open={testPlansPopoverAnchorEl}
                setOpen={setTestPlansPopoverAnchorEl}
                testPlans={testPlansByTestCase}
            />
            <DefectsPopover
                anchorEl={defectsPopoverAnchorEl}
                setAnchorEl={setDefectsPopoverAnchorEl}
                defects={defectsPopoverByTestcase}
            />
        </>
    );
};

export default TestcasesTable;
